import React, { useEffect, useMemo, useState } from "react";
import { Plus, Trash, Check, Scales, Calculator } from "@phosphor-icons/react";

// ============================================================
// IMC classification
// ============================================================
function imcCategory(v) {
  if (v == null || isNaN(v)) return { label: "—", color: "#A3A3A3" };
  if (v < 18.5) return { label: "Peso inferior al normal", color: "#FFD60A" };
  if (v < 25) return { label: "Normal", color: "#32D74B" };
  if (v < 30) return { label: "Sobrepeso", color: "#FF9500" };
  if (v < 35) return { label: "Obesidad grado 1", color: "#FF3B30" };
  if (v < 40) return { label: "Obesidad grado 2", color: "#FF3B30" };
  return { label: "Obesidad grado 3 (mórbida)", color: "#8B0000" };
}

function iccCategory(v, sex) {
  if (v == null || isNaN(v)) return { label: "—", color: "#A3A3A3" };
  const isMale = sex === "MASCULINO";
  const threshold = isMale ? 1.0 : 0.9;
  if (v < threshold) return { label: "Normal", color: "#32D74B" };
  return { label: `Riesgo (> ${threshold})`, color: "#FF3B30" };
}

function yearsOld(birthdate) {
  if (!birthdate) return null;
  const b = new Date(birthdate);
  if (isNaN(b.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - b.getFullYear();
  const m = now.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < b.getDate())) age--;
  return age;
}

const ACTIVITY_LEVELS = [
  { value: "sedentario", label: "Sedentario", factor: 0.3 },
  { value: "activa", label: "Activa", factor: 0.4 },
  { value: "muy_activa", label: "Muy activa", factor: 0.5 },
];

// ============================================================
// Main calculator component
// ============================================================

export default function MetabolicCalculator({
  targetUser,
  plan,
  canEdit,
  onChange, // called with { waist_cm, hip_cm, activity_level, activities } delta
  onUseAsGoal, // called with computed total kcal
}) {
  const profile = targetUser?.profile || {};
  const sex = (profile.sex || "MASCULINO").toUpperCase();
  const weight = Number(profile.weight_kg) || null;
  const heightCm = Number(profile.height_cm) || null;
  const age = yearsOld(profile.birthdate);

  const [waist, setWaist] = useState(plan?.waist_cm ?? "");
  const [hip, setHip] = useState(plan?.hip_cm ?? "");
  const [activityLevel, setActivityLevel] = useState(plan?.activity_level || "");
  const [activities, setActivities] = useState(plan?.activities || []);
  const [newAct, setNewAct] = useState({ name: "", minutes: "", kcal_per_min: "" });

  // Re-sync cuando el plan del server se carga/actualiza después del mount
  useEffect(() => {
    setWaist(plan?.waist_cm ?? "");
    setHip(plan?.hip_cm ?? "");
    setActivityLevel(plan?.activity_level || "");
    setActivities(plan?.activities || []);
  }, [plan?.waist_cm, plan?.hip_cm, plan?.activity_level, plan?.activities]);

  // IMC — fórmula estándar peso / talla(m)²  (user dio rangos en kg/m², así que convertimos cm→m)
  const imc = weight && heightCm ? weight / Math.pow(heightCm / 100, 2) : null;
  const imcCat = imcCategory(imc);

  // ICC
  const icc = waist && hip ? Number(waist) / Number(hip) : null;
  const iccCat = iccCategory(icc, sex);

  // Gasto Basal (Harris-Benedict)
  const basal = useMemo(() => {
    if (!weight || !heightCm || !age) return null;
    if (sex === "MASCULINO") return 66 + 13.7 * weight + 5 * heightCm - 6.8 * age;
    return 655 + 9.6 * weight + 1.7 * heightCm - 4.7 * age;
  }, [weight, heightCm, age, sex]);

  // Actividad (factor sobre gasto basal)
  const activityFactor = ACTIVITY_LEVELS.find((a) => a.value === activityLevel)?.factor || 0;
  const activityCal = basal ? basal * activityFactor : 0;

  // Actividad física (manual)
  const physicalCal = activities.reduce((acc, a) => acc + Number(a.minutes || 0) * Number(a.kcal_per_min || 0), 0);

  const total = (basal || 0) + activityCal + physicalCal;

  const syncServer = (patch) => {
    onChange?.({
      waist_cm: patch.waist_cm !== undefined ? patch.waist_cm : waist === "" ? null : Number(waist),
      hip_cm: patch.hip_cm !== undefined ? patch.hip_cm : hip === "" ? null : Number(hip),
      activity_level: patch.activity_level !== undefined ? patch.activity_level : activityLevel || null,
      activities: patch.activities !== undefined ? patch.activities : activities,
    });
  };

  const addActivity = () => {
    if (!newAct.name.trim() || !newAct.minutes || !newAct.kcal_per_min) return;
    const next = [...activities, { name: newAct.name.trim(), minutes: Number(newAct.minutes), kcal_per_min: Number(newAct.kcal_per_min) }];
    setActivities(next);
    setNewAct({ name: "", minutes: "", kcal_per_min: "" });
    syncServer({ activities: next });
  };

  const removeActivity = (idx) => {
    const next = activities.filter((_, i) => i !== idx);
    setActivities(next);
    syncServer({ activities: next });
  };

  // Warning si faltan datos básicos del perfil
  const missing = [];
  if (!weight) missing.push("peso");
  if (!heightCm) missing.push("altura");
  if (!age) missing.push("edad");

  return (
    <div className="card-base p-6" data-testid="metabolic-calculator">
      <div className="flex items-center gap-2 mb-5 pb-4 border-b border-app">
        <Calculator size={22} weight="duotone" className="text-primary-app" />
        <div>
          <h3 className="font-display font-black uppercase text-2xl tracking-tighter">Requerimiento calórico</h3>
          <div className="text-xs text-muted">IMC · ICC · Gasto basal · Actividad · Actividad física</div>
        </div>
      </div>

      {missing.length > 0 && (
        <div className="mb-5 p-3 border border-[#FFD60A]/40 bg-[#1a1505] text-[11px] text-[#FFD60A]">
          Faltan datos del perfil para calcular algunos valores: <b>{missing.join(", ")}</b>. Completalos en la solapa Perfil.
        </div>
      )}

      {/* IMC + ICC */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <div className="border border-app p-4 bg-[#0A0A0A]">
          <div className="flex items-center gap-2 mb-2">
            <Scales size={16} weight="duotone" className="text-primary-app" />
            <div className="text-[10px] uppercase tracking-widest font-bold">IMC</div>
          </div>
          <div className="font-display font-black text-4xl" style={{ color: imcCat.color }}>
            {imc != null ? imc.toFixed(1) : "—"}
          </div>
          <div className="text-xs mt-1" style={{ color: imcCat.color }}>{imcCat.label}</div>
          <div className="text-[10px] text-muted mt-2 italic">peso / talla² · {weight ? `${weight} kg` : "?"} / ({heightCm ? (heightCm / 100).toFixed(2) : "?"} m)²</div>
        </div>

        <div className="border border-app p-4 bg-[#0A0A0A]">
          <div className="flex items-center gap-2 mb-2">
            <Scales size={16} weight="duotone" className="text-primary-app" />
            <div className="text-[10px] uppercase tracking-widest font-bold">ICC · Índice cintura-cadera</div>
          </div>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-muted block mb-1">Cintura (cm)</label>
              <input
                type="number"
                step="any"
                className="input-base !py-1.5 !text-sm"
                value={waist}
                onChange={(e) => setWaist(e.target.value)}
                onBlur={() => syncServer({ waist_cm: waist === "" ? null : Number(waist) })}
                disabled={!canEdit}
                data-testid="met-waist"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-muted block mb-1">Cadera (cm)</label>
              <input
                type="number"
                step="any"
                className="input-base !py-1.5 !text-sm"
                value={hip}
                onChange={(e) => setHip(e.target.value)}
                onBlur={() => syncServer({ hip_cm: hip === "" ? null : Number(hip) })}
                disabled={!canEdit}
                data-testid="met-hip"
              />
            </div>
          </div>
          <div className="font-display font-black text-3xl" style={{ color: iccCat.color }}>
            {icc != null ? icc.toFixed(2) : "—"}
          </div>
          <div className="text-xs" style={{ color: iccCat.color }}>{iccCat.label}</div>
          <div className="text-[10px] text-muted mt-1 italic">Umbral: {sex === "MASCULINO" ? "< 1.00" : "< 0.90"}</div>
        </div>
      </div>

      {/* Gasto basal */}
      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <div className="border border-app p-4 bg-[#0A0A0A]">
          <div className="text-[10px] uppercase tracking-widest font-bold mb-2">Gasto Basal (TMB)</div>
          <div className="font-display font-black text-3xl text-[#FF3B30]">{basal ? basal.toFixed(0) : "—"}</div>
          <div className="text-[10px] text-muted">kcal / día</div>
          <div className="text-[10px] text-muted mt-2 italic">
            {sex === "MASCULINO" ? "66 + 13.7·P + 5·T − 6.8·E" : "655 + 9.6·P + 1.7·T − 4.7·E"}
          </div>
        </div>

        <div className="border border-app p-4 bg-[#0A0A0A]">
          <div className="text-[10px] uppercase tracking-widest font-bold mb-2">Actividad diaria</div>
          <select
            className="input-base !py-1.5 !text-sm mb-2"
            value={activityLevel}
            onChange={(e) => {
              setActivityLevel(e.target.value);
              syncServer({ activity_level: e.target.value || null });
            }}
            disabled={!canEdit}
            data-testid="met-activity-level"
          >
            <option value="">— Elegir —</option>
            {ACTIVITY_LEVELS.map((a) => (
              <option key={a.value} value={a.value}>{a.label} (+{Math.round(a.factor * 100)}%)</option>
            ))}
          </select>
          <div className="font-display font-black text-2xl text-[#FFD60A]">+{activityCal ? activityCal.toFixed(0) : 0}</div>
          <div className="text-[10px] text-muted">kcal extra</div>
        </div>

        <div className="border border-app p-4 bg-[#0A0A0A]">
          <div className="text-[10px] uppercase tracking-widest font-bold mb-2">Actividad física</div>
          <div className="font-display font-black text-2xl text-[#32D74B]">+{physicalCal ? physicalCal.toFixed(0) : 0}</div>
          <div className="text-[10px] text-muted">kcal de {activities.length} sesión{activities.length === 1 ? "" : "es"}</div>
        </div>
      </div>

      {/* Actividades físicas (lista editable) */}
      <div className="border border-app bg-[#0A0A0A] p-4 mb-6">
        <div className="text-[10px] uppercase tracking-widest font-bold mb-3">Sesiones de actividad física</div>
        {activities.length > 0 && (
          <div className="space-y-2 mb-3">
            {activities.map((a, i) => (
              <div key={i} className="flex items-center justify-between bg-[#141414] border border-app p-2 text-xs">
                <div className="flex-1">
                  <span className="font-bold">{a.name}</span>
                  <span className="text-muted ml-2">{a.minutes} min · {a.kcal_per_min} kcal/min = <b className="text-[#32D74B]">{(a.minutes * a.kcal_per_min).toFixed(0)} kcal</b></span>
                </div>
                {canEdit && (
                  <button type="button" onClick={() => removeActivity(i)} className="text-muted hover:text-[#FF3B30]" data-testid={`met-remove-activity-${i}`}>
                    <Trash size={14} weight="bold" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        {canEdit && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
            <input className="input-base !py-1.5 !text-sm md:col-span-2" placeholder="Actividad (ej: Fútbol)" value={newAct.name} onChange={(e) => setNewAct({ ...newAct, name: e.target.value })} data-testid="met-new-name" />
            <input type="number" step="any" className="input-base !py-1.5 !text-sm" placeholder="Min" value={newAct.minutes} onChange={(e) => setNewAct({ ...newAct, minutes: e.target.value })} data-testid="met-new-min" />
            <input type="number" step="any" className="input-base !py-1.5 !text-sm" placeholder="kcal/min" value={newAct.kcal_per_min} onChange={(e) => setNewAct({ ...newAct, kcal_per_min: e.target.value })} data-testid="met-new-kcal" />
            <button type="button" onClick={addActivity} className="btn-secondary !py-1.5 flex items-center justify-center gap-1 text-xs" data-testid="met-add-activity">
              <Plus size={12} weight="bold" /> Agregar
            </button>
          </div>
        )}
      </div>

      {/* TOTAL */}
      <div className="bg-gradient-to-r from-[#FF3B30]/10 to-[#32D74B]/10 border border-[#FF3B30] p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-primary-app font-bold">Total calórico diario</div>
          <div className="font-display font-black text-5xl text-white leading-none mt-1" data-testid="met-total">
            {total ? total.toFixed(0) : "—"}<span className="text-lg text-muted ml-2">kcal</span>
          </div>
          <div className="text-[10px] text-muted mt-2">Basal ({basal ? basal.toFixed(0) : 0}) + Actividad ({activityCal.toFixed(0)}) + Físico ({physicalCal.toFixed(0)})</div>
        </div>
        {canEdit && total > 0 && (
          <button
            type="button"
            onClick={() => onUseAsGoal?.(Math.round(total))}
            className="btn-primary flex items-center gap-2"
            data-testid="met-use-as-goal"
          >
            <Check size={16} weight="bold" /> Usar como objetivo diario
          </button>
        )}
      </div>
    </div>
  );
}
